import { useEffect, useState } from 'react'
import { CheckIcon, CopyIcon } from '@/components/icons'
import { DetailOverlay } from '@/components/overlay/DetailOverlay'
import { ProductQuickPreview } from '@/components/warehouse/ProductQuickPreview'
import { getInventoryVariant } from '@/lib/api/client/inventory'
import type { InventoryVariant } from '@/lib/api/schemas/inventory.schema'

/** The warehouse quick preview for one sold piece, loaded by its product id. */
export function UnitPiecePreview({ productId, onClose }: { productId: string; onClose: () => void }) {
  const [current, setCurrent] = useState(productId)
  const [variant, setVariant] = useState<InventoryVariant | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => setCurrent(productId), [productId])

  useEffect(() => {
    const controller = new AbortController()
    setVariant(null)
    setError(null)
    getInventoryVariant(current, controller.signal)
      .then((next) => { if (!controller.signal.aborted) setVariant(next) })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : 'Карточка изделия недоступна.')
      })
    return () => controller.abort()
  }, [current])

  return (
    <DetailOverlay modal={false} width={880} label={`Быстрый просмотр: ${variant?.title ?? 'изделие'}`} onClose={onClose}>
      {variant ? (
        <ProductQuickPreview variant={variant} providerSlots={null} onClose={onClose} onVariantPick={setCurrent} />
      ) : (
        <div className="flex h-full flex-col gap-3 border-l border-rule bg-paper p-4 text-[13px]">
          <button type="button" onClick={onClose} aria-label="Закрыть быстрый просмотр" className="self-end text-lg text-ink-muted hover:text-ink">×</button>
          {error ? <p role="alert" className="text-st-unpaid">{error}</p> : <p aria-live="polite" className="text-ink-muted">Загружаем карточку…</p>}
        </div>
      )}
    </DetailOverlay>
  )
}

/** One identifier with a one-click copy button. */
export function CopyValue({ label, value, truncate = false }: { label: string; value: string; truncate?: boolean }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return undefined
    const timer = window.setTimeout(() => setCopied(false), 1500)
    return () => window.clearTimeout(timer)
  }, [copied])

  const copy = async (event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap ${truncate ? 'min-w-0' : ''}`}>
      {label ? <span className="text-ink-faint">{label}</span> : null}
      <span className={`tab-nums text-ink ${truncate ? 'min-w-0 truncate' : ''}`} title={truncate ? value : undefined}>{value}</span>
      <button
        type="button"
        onClick={(event) => { void copy(event) }}
        aria-label={label ? `Скопировать ${label.toLocaleLowerCase('ru-RU')} ${value}` : `Скопировать ${value}`}
        title={copied ? 'Скопировано' : 'Скопировать'}
        className="inline-grid size-5 shrink-0 place-items-center rounded text-ink-muted hover:bg-bone hover:text-ink"
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </span>
  )
}
